import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import globalStyles from "../../../assets/styles/global.module.scss";
import { Autocomplete, Grid, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';
import styles from "../../../assets/styles/componentStyles/orderDetailStyles/orderDetailStyle.module.scss";
import contactStyle from '../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'
import OrderDetailsTable from './orderDetailsTable';
import OrderCard from './orderCard';
import { getPaymentFilterFlags, paymentStatusOptions } from './orderDetailsUtils';
import { useOrderDetailStore } from '../../../store/orderDetailStore';
import { useShallow } from 'zustand/react/shallow';
import { UserContext, UserContextPayload } from '../../../context/userContext';

const OrderDetails = () => {

    const { handleError, userDetail } = useContext(UserContext) as UserContextPayload
    const [selectedFilter, setSelectedFilter] = useState<string>("Show All Orders");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isOrderListLoading, setIsOrderListLoading] = useState<boolean>(false);
    const orderListRequestIdRef = useRef(0);

    const { getOrderListApi, limit } = useOrderDetailStore(useShallow((state) => ({
        getOrderListApi: state.getOrderListApi,
        limit: state.limit
    })))

    const getOrderListApiCall = useCallback(async (start: number, limit: number, searchText?: string, flag_paid_only: boolean = false, flag_not_paid_only: boolean = false) => {
        const requestId = ++orderListRequestIdRef.current;
        setIsOrderListLoading(true);

        try {
            const organizationId = userDetail?.user_organization_data?.organization_id ?? 0;
            const res = await getOrderListApi(organizationId, start, limit, searchText, flag_paid_only, flag_not_paid_only);

            if (requestId !== orderListRequestIdRef.current) return;

            if(!(res?.status)){
                handleError(res);
            }
        } finally {
            if (requestId === orderListRequestIdRef.current) {
                setIsOrderListLoading(false);
            }
        }
    }, [userDetail, getOrderListApi, handleError]);

    const debouncedSearch = useMemo(
        () => debounce((query: string, flag_paid_only: boolean, flag_not_paid_only: boolean) => {
            getOrderListApiCall(1, limit, query || undefined, flag_paid_only, flag_not_paid_only);
        }, 1500),
        [getOrderListApiCall, limit]
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value === searchQuery) return;
        setSearchQuery(value);
        const { flag_paid_only, flag_not_paid_only } = getPaymentFilterFlags(selectedFilter);
        debouncedSearch(value, flag_paid_only, flag_not_paid_only);
    };

    const onChangeOfPaymentStatus = (value: string) => {
        setSelectedFilter(value);
        const { flag_paid_only, flag_not_paid_only } = getPaymentFilterFlags(value);
        getOrderListApiCall(1, limit, searchQuery || undefined, flag_paid_only, flag_not_paid_only);
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <OrderCard />
                </Grid>
                <Grid item xs={12}>
                    <Grid container alignItems={"center"} justifyContent={"space-between"} marginBlock={"10px"}>
                        <Grid item>
                            <Autocomplete
                                className={`${contactStyle.contactFilterDropDowns} ${contactStyle.contactFilterAutoCompleteMargin} ${styles.filterAutoComplete}`}
                                options={paymentStatusOptions}
                                getOptionLabel={(option) => option}
                                disableClearable
                                value={selectedFilter}
                                onChange={(_: React.SyntheticEvent, value: string) => onChangeOfPaymentStatus(value)}
                                renderInput={params => {
                                    const { InputProps, ...restParams } = params;
                                    const { startAdornment, ...restInputProps } = InputProps;
                                    return (
                                        <TextField
                                            {...restParams}
                                            placeholder='Search by Payment'
                                            InputProps={{
                                                ...restInputProps,
                                                startAdornment: (
                                                    <div style={{
                                                        maxHeight: "79px",
                                                        overflowY: 'auto',
                                                    }}
                                                    >
                                                        {startAdornment}
                                                    </div>
                                                ),
                                            }}
                                        />
                                    );
                                }}
                            />
                        </Grid>
                        <Grid item>
                            <Grid container justifyContent={{ xs: "start", sm: "end" }}>
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search by Phone Number"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                        className={`${styles.searchPhoneNumberTextField} ${globalStyles.fullwidth}`}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon className={styles.searchIcon} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} mt={2}>
                    <OrderDetailsTable
                        getOrderListApiCall={getOrderListApiCall}
                        searchQuery={searchQuery}
                        selectedFilter={selectedFilter}
                        isOrderListLoading={isOrderListLoading}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default OrderDetails
